"use client";

import NavLogo from "@/assets/logos/logo-lucidity-mobile.svg";
import SimpleNavLogo from "@/assets/logos/logo-simple-lucidity.svg";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import Image from "next/image";
import Link from "next/link";
import React, { useEffect, useState } from "react";

import { Drawer, DrawerContent, DrawerTrigger } from "@/components/ui/drawer";
import { getNotifications } from "@/services/api/Notification";
import {
  ArrowUpRight,
  ArticleMedium,
  Bell,
  Brain,
  House,
  LampPendant,
  List,
  MicrophoneStage,
  MoneyWavy,
  MonitorPlay,
  Newspaper,
  NewspaperClipping,
  Panorama,
  Plus,
  SignIn,
  User,
  Wrench,
  X,
} from "@phosphor-icons/react/dist/ssr";
import { useRouter } from "next/router";
import { Rocket } from "@phosphor-icons/react";

const menus = [
  {
    name: "Homepage",
    icon: <House weight='duotone' className='size-6' />,
    url: "/",
  },
  {
    name: "Special Reports",
    icon: <Newspaper weight='duotone' className='size-6' />,
    url: "/reports",
  },
  {
    name: "Podcasts",
    icon: <MicrophoneStage weight='duotone' className='size-6' />,
    url: "/podcasts-masterclasses",
  },
  {
    name: "Opinions",
    icon: <Brain weight='duotone' className='size-6' />,
    url: "/opinions",
  },
  {
    name: "Videos",
    icon: <MonitorPlay weight='duotone' className='size-6' />,
    url: "/videos",
  },
  {
    name: "Infobytes",
    icon: <Panorama weight='duotone' className='size-6' />,
    url: "/infobytes",
  },
  {
    name: "Spotlights",
    icon: <LampPendant weight='duotone' className='size-6' />,
    url: "/spotlights",
  },
  {
    name: "Articles",
    icon: <ArticleMedium weight='duotone' className='size-6' />,
    url: "/articles",
  },
  {
    name: "News",
    icon: <NewspaperClipping weight='duotone' className='size-6' />,
    url: "/news",
  },
  {
    name: "Unicorn Tracker",
    icon: <Rocket weight='duotone' className='size-6' />,
    url: "/unicorn-tracker",
  },
  {
    name: "Tools & Resources",
    icon: <Wrench weight='duotone' className='size-6' />,
    items: [
      {
        name: "Investor & Angel Database",
        url: "/investors",
      },
      {
        name: "Startup Database",
        url: "/startups",
      },
      {
        name: "My Messages",
        url: "/messages",
      },
      {
        name: "Watchlists",
        url: "/account/watchlist",
      },
      {
        name: "Bookmark",
        url: "/account/bookmarks",
      },
      {
        name: "Matchmaking Tools",
        url: "/investors",
      },
      {
        name: "Startup Events",
        url: "/startup-events",
      },
      {
        name: "Newsletter Archive",
        url: "/email-archive",
      },
      {
        name: "Startup Toolbox",
        url: "/toolbox",
      },
      {
        name: "Fundraising Readiness Test",
        url: "https://tally.so/r/GxW7Lo",
      },
    ],
  },
  {
    name: "Pricing",
    icon: <MoneyWavy weight='duotone' className='size-6' />,
    url: "/pricing",
  },
];

export default function TopNavigationMobile({ user, logout, handleOpenLogin }) {
  const [open, setOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const router = useRouter();

  useEffect(() => {
    const fetchNotifications = async () => {
      if (!user) return;

      try {
        setIsLoading(true);
        const response = await getNotifications();
        if (response?.data?.success) {
          setUnreadCount(response.data.unread_count);
        }
      } catch (error) {
        console.error("Error fetching notifications:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchNotifications();
  }, [user]);

  return (
    <div className='sticky top-0 bg-[#1D1D1F]/90 backdrop-blur-md z-50 h-16 flex items-center'>
      <div className='container flex items-center justify-between'>
        <Link href={"/"} className='relative h-10 aspect-[9/4]'>
          <Image
            src={NavLogo.src}
            alt='Lucidity Insights'
            layout='fill'
            objectFit='cover'
          />
        </Link>
        <div className='flex items-center justify-end flex-1 gap-4'>
          {user ? (
            <>
              <Link href='/account/notifications' className='relative'>
                <Bell weight='bold' className='size-6' />
                {unreadCount > 0 && (
                  <span className='absolute inline-flex items-center justify-center size-4 leading-none text-[9px] font-bold bg-red-500 rounded-full -right-1 -top-1'>
                    {unreadCount}
                  </span>
                )}
              </Link>
              {/* <Link href='/messages' className='relative'>
                <ChatCircleDots weight='bold' className='size-6' />
                <span className='absolute inline-flex items-center justify-center size-4 leading-none text-[9px] font-bold bg-red-500 rounded-full -right-1 -top-1'>
                  49
                </span>
              </Link> */}
              <Link href='/account'>
                <User weight='bold' className='size-6' />
              </Link>
            </>
          ) : (
            <>
              <Link
                href='/pricing'
                className='bg-white text-background h-auto text-[13px] leading-none !font-semibold tracking-tight px-4 py-2.5 rounded-full'
              >
                Pricing
              </Link>
              <button type='button' onClick={handleOpenLogin}>
                <SignIn weight='bold' className='size-6' />
              </button>
            </>
          )}
          <Drawer open={open} onOpenChange={setOpen} direction='right'>
            <DrawerTrigger asChild>
              <button type='button'>
                <List weight='bold' className='size-6' />
              </button>
            </DrawerTrigger>
            <DrawerContent
              showHandle={false}
              className='h-[100dvh] text-sm font-medium tracking-tight text-left bg-white rounded-none text-background/50'
            >
              <div className='flex items-center justify-between border-b border-background/5 px-5 py-2.5'>
                <Link href='/' className='relative h-8 aspect-[7/8]'>
                  <Image
                    src={SimpleNavLogo.src}
                    alt='Lucidity Insights'
                    layout='fill'
                    objectFit='cover'
                  />
                </Link>
                <span className='text-sm tracking-normal text-background/40'>
                  Main Menu
                </span>
                <button
                  type='button'
                  className='inline-flex items-center justify-center flex-shrink-0 transition-colors border rounded-full border-gray-300/70 bg-gradient-to-b from-white to-background/10 size-8'
                  onClick={() => setOpen(false)}
                >
                  <X weight='bold' className='text-base text-background/50' />
                </button>
              </div>
              <div className='max-h-full px-5 pb-1.5 overflow-y-auto divide-y divide-dashed divide-background/20 text-background'>
                {menus.map((menu, index) =>
                  menu.items ? (
                    <Accordion
                      type='single'
                      collapsible
                      key={index}
                      defaultValue={`item-${index}`}
                    >
                      <AccordionItem value={`item-${index}`}>
                        <AccordionTrigger asChild>
                          <div className='flex items-center !py-3.5'>
                            <div className='flex items-center flex-1 gap-3 text-lg font-semibold'>
                              {menu.icon}
                              <span className='leading-none tracking-tight translate-y-px'>
                                {menu.name}
                              </span>
                            </div>
                            <Plus className='opacity-60 size-5' />
                          </div>
                        </AccordionTrigger>
                        <AccordionContent>
                          <div className='ml-[11px] gap-3.5 mt-1 pl-6 border-l border-background/20 flex flex-col'>
                            {menu.items.map((sub, subIndex) => {
                              const isWatchlist =
                                sub.url === "/account/watchlist";
                              const isBookmark =
                                sub.url === "/account/bookmarks";
                              const isMessages = sub.url === "/messages";

                              const handleClick = () => {
                                if (user) {
                                  router.push(sub.url);
                                } else {
                                  handleOpenLogin();
                                }
                              };
                              return isWatchlist || isBookmark || isMessages ? (
                                <button
                                  key={index}
                                  onClick={handleClick}
                                  className='text-sm font-medium tracking-normal text-left text-background/60'
                                >
                                  {sub.name}
                                </button>
                              ) : (
                                <Link
                                  key={index}
                                  href={sub.url}
                                  className='no-underline'
                                  {...(sub.url.startsWith("http") && {
                                    target: "_blank",
                                    rel: "noopener noreferrer",
                                  })}
                                >
                                  <span className='text-sm font-medium tracking-normal text-background/60'>
                                    {sub.name}
                                  </span>
                                </Link>
                              );
                            })}
                          </div>
                        </AccordionContent>
                      </AccordionItem>
                    </Accordion>
                  ) : (
                    <Link
                      href={
                        menu.url === "/unicorn-tracker" && !user
                          ? "/unicorn-tracker/intro"
                          : menu.url
                      }
                      className='flex items-center py-3.5'
                      key={index}
                    >
                      <span className='flex items-center flex-1 gap-3 text-lg font-semibold'>
                        {menu.icon}
                        <span className='leading-none tracking-tight translate-y-px'>
                          {menu.name}
                        </span>
                      </span>
                      <ArrowUpRight className='opacity-40 size-5' />
                    </Link>
                  ),
                )}
              </div>
            </DrawerContent>
          </Drawer>
        </div>
      </div>
    </div>
  );
}
